import React from 'react';
import { Link } from 'react-router-dom';
import { formatDublinDateTime, formatDublinTime } from '../../utils/date';
import { FeedItem } from '../../types';
import ClickablePhoto from './ClickablePhoto';

/**
 * One row of the parent "Recent feed" — a merged moment or observation.
 *
 * Same white card chrome as everything else on the parent home
 * (`bg-white rounded-lg shadow p-4`). The only per-kind difference is the
 * chip colour: PINK for a moment (matching the moment accent used in the
 * staff share modal), BLUE (primary) for an observation. Photos, when the
 * item carries them, render as a small thumbnail strip.
 *
 * Passive by construction: rendering a feed item never marks anything read.
 *
 * `href` turns the whole card into a link to the item. `showDate` puts the
 * date beside the time — for flat lists that have no day heading above them.
 */
const FeedItemCard: React.FC<{ item: FeedItem; href?: string; showDate?: boolean }> = ({
  item,
  href,
  showDate = false,
}) => {
  const isMoment = item.kind === 'MOMENT';
  const chipClasses = isMoment ? 'bg-pink-100 text-pink-700' : 'bg-primary-100 text-primary-700';
  const testId = `feed-item-${item.kind.toLowerCase()}-${item.id}`;

  const content = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${chipClasses}`}
        >
          {item.kind}
        </span>
        <span className="text-xs text-gray-500 truncate">
          {item.authorName}
          {' · '}
          {showDate ? formatDublinDateTime(item.createdAt) : formatDublinTime(item.createdAt)}
        </span>
      </div>

      {item.text && <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.text}</p>}

      {item.photoUrls.length > 0 && (
        // When the card is a link, a photo tap must open the lightbox, not
        // follow the link: ClickablePhoto is a <button> nested in the <a>.
        <div
          className="mt-2 flex gap-2 flex-wrap"
          onClick={
            href
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : undefined
          }
        >
          {item.photoUrls.map((url, idx) => (
            <ClickablePhoto
              key={idx}
              url={url}
              alt={
                item.text
                  ? `Photo from ${item.authorName}`
                  : `${item.kind === 'MOMENT' ? 'Moment' : 'Observation'} photo`
              }
              downloadName={`${
                item.kind === 'MOMENT' ? 'moment' : 'observation'
              }-${item.createdAt.slice(0, 10)}`}
              className="h-20 w-20 rounded-md"
            />
          ))}
        </div>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        data-testid={testId}
        className="block bg-white rounded-lg shadow p-4 hover:shadow-md"
      >
        {content}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className="bg-white rounded-lg shadow p-4">
      {content}
    </div>
  );
};

export default FeedItemCard;
